import { StackIcon } from "@radix-ui/react-icons";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { twMerge } from "tailwind-merge";
import { CartSegment, CartStatus } from "../lib/types";
import { DashboardSystemCurrentTimeResponse } from "@/shared/api";

type ColumnOverlayProps = {
  selectedSegment: CartSegment;
  annotatedCartStatus: CartStatus;
  orderLinesStatus: CartStatus;
  zoneBasedPredictionStatus: CartStatus;
  pinBasedPredictionStatus: CartStatus;
  systemCurrentTimeState?: {
    isLoading: boolean;
    data?: DashboardSystemCurrentTimeResponse;
  };
  journeyCount: number | undefined;
  isLoading: boolean;
};

export function ColumnOverlay({
  selectedSegment,
  annotatedCartStatus,
  orderLinesStatus,
  zoneBasedPredictionStatus,
  pinBasedPredictionStatus,
  journeyCount,
  isLoading,
}: ColumnOverlayProps) {
  return (
    <div className="flex justify-center absolute top-0 left-0 w-full h-full z-10 pointer-events-none">
      {selectedSegment === CartSegment.Annotated ? (
        <Column type="Annotated cart" status={annotatedCartStatus} />
      ) : (
        <Column
          type="Order lines"
          status={orderLinesStatus}
          nullMessage="Null"
        />
      )}

      <Column
        type="Prediction"
        status={isLoading ? CartStatus.Pending : zoneBasedPredictionStatus}
      />
      <Column
        type="Prediction"
        status={isLoading ? CartStatus.Pending : pinBasedPredictionStatus}
        journeyCreated={journeyCount !== undefined && journeyCount > 0}
      />
    </div>
  );
}

type ColumnProps = {
  status: CartStatus;
  journeyCreated?: boolean;
  type: string;
  emptyMessage?: string;
  nullMessage?: string;
};

function Column({
  status,
  journeyCreated,
  type,
  emptyMessage,
  nullMessage,
}: ColumnProps) {
  const shouldCover = status !== CartStatus.Ready;

  return (
    <div
      className={twMerge([
        "w-1/3 min-w-1/3 border-r align-top py-2 px-0 pointer-events-none first:rounded-bl-xl last:rounded-br-xl first:border-l",
        shouldCover && "h-[calc(100dvh-555px)] bg-white",
      ])}
    >
      {journeyCreated === false ? (
        <ColumnStatus
          type={type}
          status={CartStatus.Empty}
          emptyMessage={emptyMessage}
          nullMessage={nullMessage}
        />
      ) : (
        <ColumnStatus
          type={type}
          status={status}
          emptyMessage={emptyMessage}
          nullMessage={nullMessage}
        />
      )}
    </div>
  );
}

type ColumnStatusProps = Omit<ColumnProps, "journeyCreated">;

function ColumnStatus({
  status,
  type,
  emptyMessage,
  nullMessage,
}: ColumnStatusProps) {
  switch (status) {
    case CartStatus.Null:
      return <NotReadyColumn type={type} customMessage={nullMessage} />;
    case CartStatus.Empty:
      return <EmptyColumn customMessage={emptyMessage} />;
    case CartStatus.Pending:
      return <PendingColumn />;
    case CartStatus.Ready:
      return null;
  }
}

function NotReadyColumn({
  type = "Prediction",
  customMessage,
}: {
  type: string;
  customMessage?: string;
}) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      className="h-full"
    >
      <Text color="gray" size="2">
        {customMessage || `${type} is not ready yet`}
      </Text>
    </Flex>
  );
}

function EmptyColumn({ customMessage }: { customMessage?: string }) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      className="h-full"
    >
      <StackIcon color="var(--gray-11)" width="1.5rem" height="1.5rem" />
      <Text color="gray" size="2">
        {customMessage || "Empty cart"}
      </Text>
    </Flex>
  );
}

function PendingColumn() {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      className="h-full"
    >
      <Spinner />
      <Text color="gray" size="2">
        Loading data...
      </Text>
    </Flex>
  );
}
